import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, DialogActions, IconButton } from '@mui/material';
import styles from './styles.module.css';
import CloseIcon from '@mui/icons-material/Close';
import { AlertMessagesContext } from 'react-alert-messages';
import ItemService from 'services/ItemService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import DataTable from 'components/utils/DataTable';
import ShopsHelper from 'helpers/ShopsHelper';
import { ShopContext } from 'Context/ShopContext';
import PrintIcon from '@mui/icons-material/Print';
import CsvHelper from 'helpers/CsvHelper';
import { saveAs } from 'file-saver';

export default function ConfirmProductionDialog({ handleClose, handleSuccess, data = {} }) {
  const { t: tCommon } = useTranslation('common');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [isProcessing, setIsProcessing] = useState(false);

  const subItemsMap = (data?.items || []).reduce((acc, item) => {
    if (Array.isArray(item?.sub_item)) {
      item.sub_item.forEach((subItem) => {
        const key = subItem.item_id;
        const qty = subItem.quantity;
        const cost = subItem.cost || 0;
        const totalCost = subItem.totalCost || qty * cost;

        if (acc[key]) {
          acc[key].quantity += qty;
          acc[key].totalCost += totalCost;
        } else {
          acc[key] = {
            id: subItem.item_id,
            name: subItem.name,
            quantity: qty,
            cost: cost,
            totalCost: totalCost,
            batch_data: subItem.batch_data,
          };
        }
      });
    }
    return acc;
  }, {});

  const CSV_COLUMNS = {
    name: 'Name',
    quantity: 'Quantity',
    cost: 'Cost per Unit',
    totalCost: 'Total Cost',
  };

  const toCsvBtnPressed = async () => {
    const item = Object.values(subItemsMap);
    const csv = CsvHelper.getString(item, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `MATERIALS-${dateTime}.csv`);

    postAlertMessage({
      text: tCommon('Exported to excel success'),
      type: 'success',
    });
  };

  const headerData = [
    {
      label: tCommon('Item Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Quantity'),
      id: 'quantity',
      type: 'callback',
      viewRender: (item) => parseFloat(item.quantity.toFixed(3)),
    },
    {
      label: tCommon('Cost'),
      id: 'cost',
      type: 'text',
    },
    {
      label: tCommon('Total Cost'),
      id: 'totalCost',
      type: 'callback',
      viewRender: (item) => ShopsHelper.getAmountFormatted(shop, item.totalCost),
    },
  ];

  const _getSummary = (subItemsMap) => {
    const items = Object.values(subItemsMap);
    let totalCost = 0;
    items.forEach((item) => {
      totalCost += item.totalCost;
    });

    return {
      totalCost: ShopsHelper.getAmountFormatted(shop, totalCost),
    };
  };

  const handleActionButton = async () => {
    setIsProcessing(true);
    try {
      const productionData = data?.items.map((item) => ({
        id: item.item?.id,
        name: item.item?.name,
        quantity: item.quantity,
        sub_item: item.sub_item,
      }));
      await ItemService.createProductionEntry({
        items: productionData,
        created_at: DateTimeHelpers.getDateYMD(data.date),
        config: { raw_materials: Object.values(subItemsMap) },
        batch_name: data.batch_name,
        warehouse_id: data.warehouse_id,
      });

      postAlertMessage({ text: 'Production processed successfully', type: 'success' });
      handleSuccess();
    } catch (error) {
      postAlertMessage({ text: error.message || 'Failed to confirm production', type: 'failed' });
      setIsProcessing(false);
      return;
    }
    handleClose();
  };

  const summary = _getSummary(subItemsMap);

  return (
    <div className={styles.popUp}>
      <div className={styles.contentWrapper} style={{ width: '50%' }}>
        <div className={styles.headSec}>
          <h2 className={styles.editTitle}>{tCommon('Confirm Production')}</h2>
          <div>
            <IconButton size="medium" onClick={toCsvBtnPressed}>
              <PrintIcon fontSize="small" />
            </IconButton>
            <IconButton size="medium" onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </div>
        </div>
        <div className={styles.itemsList}>
          <DataTable
            columns={headerData}
            rows={Object.values(subItemsMap)}
            summary={{
              name: tCommon('Total'),
              totalCost: `${shop?.currency || ''} ${summary.totalCost}`,
            }}
          />
        </div>
        <div className={styles.footSec} style={{ padding: '30px 5px' }}>
          <DialogActions>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleClose}
              className={styles.closeBtn}
              disabled={isProcessing}
            >
              {tCommon('Close')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              style={{ backgroundColor: '#00a65a' }}
              onClick={handleActionButton}
              disabled={isProcessing}
            >
              {tCommon('Confirm Production')}
            </Button>
          </DialogActions>
        </div>
      </div>
    </div>
  );
}
